import { useState, useEffect } from 'react';

export default function CookieConsent() {
  const [isVisible, setIsVisible] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
    functional: false
  });
  const [showPreferences, setShowPreferences] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem('cookie-consent');
    if (!consent) {
      setIsVisible(true);
    }
  }, []);

  const handleAcceptAll = () => {
    const consentData = {
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookie-consent', JSON.stringify(consentData));
    setIsVisible(false);
  };

  const handleRejectAll = () => {
    const consentData = {
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookie-consent', JSON.stringify(consentData));
    setIsVisible(false);
  };

  const handleSavePreferences = () => {
    const consentData = {
      ...preferences,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookie-consent', JSON.stringify(consentData));
    setIsVisible(false);
  };

  const handlePreferenceChange = (type: string, value: boolean) => {
    setPreferences(prev => ({
      ...prev,
      [type]: value
    }));
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 animate-slide-up">
      <div className="max-w-7xl mx-auto bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg shadow-2xl">
        <div className="p-6">
          {showPreferences ? (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-white">
                  Cookie Preferences
                </h2>
                <button
                  onClick={() => setShowPreferences(false)}
                  className="text-white hover:text-gray-200 cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-white/10 backdrop-blur-sm p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-white">Necessary</label>
                    <div className="w-10 h-6 bg-white/30 rounded-full relative">
                      <div className="w-4 h-4 bg-white rounded-full absolute top-1 right-1 shadow-sm"></div>
                    </div>
                  </div>
                  <p className="text-xs text-white/80">Required for basic site functionality</p>
                </div>

                <div className="bg-white/10 backdrop-blur-sm p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-white">Analytics</label>
                    <div 
                      className={`w-10 h-6 rounded-full relative cursor-pointer transition-colors ${
                        preferences.analytics ? 'bg-green-400' : 'bg-white/30'
                      }`}
                      onClick={() => handlePreferenceChange('analytics', !preferences.analytics)}
                    >
                      <div className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-transform shadow-sm ${
                        preferences.analytics ? 'translate-x-4' : 'translate-x-1'
                      }`}></div>
                    </div>
                  </div>
                  <p className="text-xs text-white/80">Help us improve our website</p>
                </div>

                <div className="bg-white/10 backdrop-blur-sm p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-white">Marketing</label>
                    <div 
                      className={`w-10 h-6 rounded-full relative cursor-pointer transition-colors ${
                        preferences.marketing ? 'bg-green-400' : 'bg-white/30'
                      }`}
                      onClick={() => handlePreferenceChange('marketing', !preferences.marketing)}
                    >
                      <div className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-transform shadow-sm ${
                        preferences.marketing ? 'translate-x-4' : 'translate-x-1'
                      }`}></div>
                    </div>
                  </div>
                  <p className="text-xs text-white/80">Personalized ads and content</p>
                </div>

                <div className="bg-white/10 backdrop-blur-sm p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-white">Functional</label>
                    <div 
                      className={`w-10 h-6 rounded-full relative cursor-pointer transition-colors ${
                        preferences.functional ? 'bg-green-400' : 'bg-white/30'
                      }`}
                      onClick={() => handlePreferenceChange('functional', !preferences.functional)}
                    >
                      <div className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-transform shadow-sm ${
                        preferences.functional ? 'translate-x-4' : 'translate-x-1'
                      }`}></div>
                    </div>
                  </div>
                  <p className="text-xs text-white/80">Enhanced website features</p>
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowPreferences(false)}
                  className="bg-white/20 text-white py-2.5 px-6 rounded-md font-medium hover:bg-white/30 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSavePreferences}
                  className="bg-white text-indigo-600 py-2.5 px-6 rounded-md font-medium hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Save Preferences
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-start gap-4 flex-1">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-cookie-line text-white text-2xl"></i>
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-white mb-2">
                    We use cookies
                  </h2>
                  <p className="text-white/90 text-sm leading-relaxed">
                    This website uses cookies to help the website function and track how you interact with it. 
                    We'll only use cookies if you accept. {' '}
                    <button 
                      className="text-white underline hover:text-gray-200 cursor-pointer font-medium"
                      onClick={() => window.open('/privacy-policy', '_blank')}
                    >
                      Learn more
                    </button>
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <button
                  onClick={() => setShowPreferences(true)}
                  className="bg-white/20 text-white py-2.5 px-6 rounded-md font-medium hover:bg-white/30 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Preferences
                </button>
                <button
                  onClick={handleRejectAll}
                  className="bg-white/20 text-white py-2.5 px-6 rounded-md font-medium hover:bg-white/30 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Reject All
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="bg-white text-indigo-600 py-2.5 px-6 rounded-md font-medium hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Accept All
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}